import React from 'react';
import {
    Stack, Lightning, Gauge, Buildings, MapTrifold, TrendUp,
    CaretDown, CaretUp, Info, ArrowRight, Sliders,
} from '@phosphor-icons/react';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';

// ============================================
// TYPES
// ============================================

export type SmartSeqMode = 'EOBT' | 'CTOT' | 'FCFS' | 'ASAT' | null;

export interface ScenarioConfig {
    name: string;
    description: string;
    project_id: string;

    is_baseline: boolean;

    ams_enabled: boolean;
    ams_config: {
        handover_latency_reduction: number;
        smart_sequencing_mode: SmartSeqMode;
        // SOP — merged into AMS section
        simultaneous_pushback: boolean;
        contiguous_pushback: boolean;
        simultaneous_contiguous_pushback: boolean;
        dynamic_routing: boolean;
        adaptive_routing: boolean;
        safety_blocking: boolean;
    };

    fastgate_enabled: boolean;
    fastgate_config: {
        pushback_reduction: number;
        eligible_types: string[];
    };

    turnaround_monitoring_enabled: boolean;
    turnaround_monitoring_config: {
        provider: 'assaia' | 'zestiot' | 'other';
        turnaround_reduction: number;
    };

    infra_changes_enabled: boolean;
    infra_changes_config: {
        infrastructure_types: string[];
        new_taxiways: any[];
        new_stands: any[];
        tcf_reduction: number;
        what_improves: string;
        how_improves: string;
        magnitude: string;
    };

    standard_routes_enabled: boolean;
    standard_routes_config: {
        routes_description: string;
    };

    dacams_improvements_enabled: boolean;
    simulation_parameters?: Record<string, any>;
}

interface ScenarioBuilderProps {
    config: ScenarioConfig;
    onChange: (config: ScenarioConfig) => void;
    projects: { id: string; name: string; iata_code?: string }[];
}

// ============================================
// CONSTANTS
// ============================================

const INFRA_TYPES = ['Terminal', 'Plataforma', 'Estacionamientos', 'Calles de rodaje', 'Pistas'];

const SMART_SEQ_OPTIONS: Array<{ label: string; value: SmartSeqMode }> = [
    { label: 'Off', value: null },
    { label: 'EOBT', value: 'EOBT' },
    { label: 'CTOT', value: 'CTOT' },
    { label: 'FCFS', value: 'FCFS' },
    { label: 'ASAT', value: 'ASAT' },
];

// ============================================
// COMPONENT
// ============================================

export const ScenarioBuilder: React.FC<ScenarioBuilderProps> = ({ config, onChange, projects }) => {
    const { theme } = useTheme();
    const { language } = useLanguage();
    const isDark = theme === 'dark';

    const textMain = 'text-[var(--text-primary)]';
    const textSub = 'text-[var(--text-secondary)]';
    const cardBg = isDark ? 'bg-[var(--surface)] border-[var(--border)]' : 'bg-white border-[var(--border)]';
    const inputBg = isDark ? 'bg-white border-[var(--border)]' : 'bg-gray-50 border-gray-300';
    const inputText = 'text-gray-800';
    const panelBg = isDark ? 'border-[var(--border)] bg-[var(--surface-inset)]' : 'border-[var(--border)] bg-gray-50';

    const [expandedSections, setExpandedSections] = React.useState<Record<string, boolean>>({
        ams: false,
        fastgate: false,
        turnaround: false,
        infra: false,
        standard_routes: false,
    });

    const toggleSection = (key: string) => {
        setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const updateConfig = (path: string, value: any) => {
        const keys = path.split('.');
        const newConfig = { ...config };
        let current: any = newConfig;
        for (let i = 0; i < keys.length - 1; i++) {
            current[keys[i]] = { ...current[keys[i]] };
            current = current[keys[i]];
        }
        current[keys[keys.length - 1]] = value;
        onChange(newConfig);
    };

    // ---- Sub-components ----

    const Toggle = ({ enabled, onToggle, label, description }: {
        enabled: boolean;
        onToggle: () => void;
        label: string;
        description?: string;
    }) => (
        <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0 pr-3">
                <span className={`font-medium ${textMain}`}>{label}</span>
                {description && <p className={`text-xs mt-0.5 ${textSub}`}>{description}</p>}
            </div>
            <button
                type="button"
                onClick={onToggle}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
                    enabled ? 'bg-[#F26430]' : isDark ? 'bg-gray-600' : 'bg-gray-300'
                }`}
            >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    enabled ? 'translate-x-6' : 'translate-x-1'
                }`} />
            </button>
        </div>
    );

    function PillGroup<T extends string | null>({
        options, value, onChange: onPillChange,
    }: {
        options: Array<{ label: string; value: T }>;
        value: T;
        onChange: (v: T) => void;
    }): React.ReactElement {
        return (
            <div className="flex flex-wrap gap-1">
                {options.map(opt => (
                    <button
                        key={String(opt.label)}
                        type="button"
                        onClick={() => onPillChange(opt.value)}
                        className={`px-3 py-1 rounded text-xs font-bold transition-colors ${
                            value === opt.value
                                ? 'bg-[#F26430] text-white'
                                : isDark
                                    ? 'bg-[var(--surface-raised)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                    >
                        {opt.label}
                    </button>
                ))}
            </div>
        );
    }

    const SectionHeader = ({
        icon: Icon, title, subtitle, enabled, onToggle, expanded, onExpand, color,
    }: any) => (
        <div className={`p-4 rounded-lg border ${cardBg} ${enabled ? `ring-2 ring-${color}-500/40` : ''}`}>
            <div className="flex items-center gap-4">
                <div className={`p-3 rounded-lg flex-shrink-0 ${
                    enabled
                        ? `bg-${color}-500/20 text-${color}-500`
                        : isDark
                            ? 'bg-[var(--surface-inset)] text-[var(--text-secondary)]'
                            : 'bg-gray-100 text-gray-400'
                }`}>
                    <Icon size={22} weight="duotone" />
                </div>
                <div className="flex-1 min-w-0">
                    <Toggle enabled={enabled} onToggle={onToggle} label={title} description={subtitle} />
                </div>
                {enabled && (
                    <button
                        type="button"
                        onClick={onExpand}
                        className={`p-2 rounded-lg hover:bg-gray-500/10 flex-shrink-0 ${textSub}`}
                    >
                        {expanded ? <CaretUp size={18} /> : <CaretDown size={18} />}
                    </button>
                )}
            </div>
        </div>
    );

    return (
        <div className="space-y-6">

            {/* 1. BASIC CONFIG */}
            <div className={`p-6 rounded-xl border ${cardBg}`}>
                <h3 className={`text-lg font-bold mb-4 ${textMain}`}>
                    {language === 'es' ? '1. Configuración Básica' : '1. Basic Configuration'}
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className={`block text-sm font-medium mb-2 ${textSub}`}>
                            {language === 'es' ? 'Nombre del Escenario' : 'Scenario Name'}
                        </label>
                        <input
                            type="text"
                            value={config.name}
                            onChange={(e) => updateConfig('name', e.target.value)}
                            placeholder={language === 'es' ? 'Ej: Escenario AMS + FASTGate' : 'Ex: AMS + FASTGate Scenario'}
                            className={`w-full px-4 py-3 rounded-lg border ${inputBg} ${inputText} focus:ring-2 focus:ring-[#F26430] focus:border-[#F26430]`}
                        />
                    </div>
                    <div>
                        <label className={`block text-sm font-medium mb-2 ${textSub}`}>
                            {language === 'es' ? 'Proyecto DACAMS Base' : 'Base DACAMS Project'}
                        </label>
                        <select
                            value={config.project_id}
                            onChange={(e) => updateConfig('project_id', e.target.value)}
                            className={`w-full px-4 py-3 rounded-lg border ${inputBg} ${inputText} focus:ring-2 focus:ring-[#F26430]`}
                        >
                            <option value="">{language === 'es' ? 'Seleccionar proyecto...' : 'Select project...'}</option>
                            {projects.map(p => (
                                <option key={p.id} value={p.id}>
                                    {p.iata_code ? `[${p.iata_code}] ` : ''}{p.name}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="mt-4">
                    <label className={`block text-sm font-medium mb-2 ${textSub}`}>
                        {language === 'es' ? 'Descripción' : 'Description'}
                    </label>
                    <textarea
                        value={config.description}
                        onChange={(e) => updateConfig('description', e.target.value)}
                        rows={2}
                        placeholder={language === 'es' ? 'Describe el objetivo de este escenario...' : 'Describe the scenario objective...'}
                        className={`w-full px-4 py-3 rounded-lg border ${inputBg} ${inputText} focus:ring-2 focus:ring-[#F26430]`}
                    />
                </div>

                <div className={`mt-4 p-4 rounded-lg border ${isDark ? 'border-[var(--border)] bg-[var(--surface-raised)]' : 'border-[var(--border)] bg-gray-50'}`}>
                    <Toggle
                        enabled={config.is_baseline}
                        onToggle={() => updateConfig('is_baseline', !config.is_baseline)}
                        label={language === 'es' ? 'Escenario Baseline (As-Is)' : 'Baseline Scenario (As-Is)'}
                        description={language === 'es' ? 'Marcar como escenario de referencia sin mejoras' : 'Mark as reference scenario without improvements'}
                    />
                </div>
            </div>

            {/* 2. TO-BE OPTIONS */}
            <div className={`p-6 rounded-xl border ${cardBg}`}>
                <h3 className={`text-lg font-bold mb-1 ${textMain}`}>
                    {language === 'es' ? '2. Opciones To-Be (Mejoras)' : '2. To-Be Options (Improvements)'}
                </h3>
                <p className={`text-sm mb-5 ${textSub}`}>
                    {language === 'es'
                        ? 'Activa las mejoras que quieres simular. Puedes combinar múltiples opciones.'
                        : 'Enable the improvements you want to simulate. You can combine multiple options.'}
                </p>

                <div className="space-y-4">

                    {/* AMS */}
                    <div>
                        <SectionHeader
                            icon={Stack}
                            title={language === 'es' ? 'Con Apron Management Service (AMS)' : 'With Apron Management Service (AMS)'}
                            subtitle={language === 'es' ? 'Gestión profesional de plataforma con SOP integrados' : 'Professional apron management with integrated SOPs'}
                            enabled={config.ams_enabled}
                            onToggle={() => updateConfig('ams_enabled', !config.ams_enabled)}
                            expanded={expandedSections.ams}
                            onExpand={() => toggleSection('ams')}
                            color="emerald"
                        />
                        {config.ams_enabled && expandedSections.ams && (
                            <div className={`mt-2 p-5 rounded-lg border ml-4 ${panelBg}`}>

                                {/* Workflow reference */}
                                <div className={`mb-5 p-3 rounded-lg border text-xs ${isDark ? 'bg-[var(--surface)] border-[var(--border)]' : 'bg-white border-gray-200'}`}>
                                    <div className={`font-semibold mb-2 flex items-center gap-1 ${textSub}`}>
                                        <Info size={12} />
                                        {language === 'es' ? 'Flujo operativo AMS (referencia)' : 'AMS operational flow (reference)'}
                                    </div>
                                    <div className="space-y-1.5">
                                        <div className="flex items-center gap-1 flex-wrap">
                                            <span className={`font-semibold text-emerald-500 w-16 flex-shrink-0`}>Salidas</span>
                                            {['EOBT/Slot', 'ATC OK', 'Req. P/B', 'Auth. AMS', 'Rodaje', 'Pista'].map((step, i, arr) => (
                                                <React.Fragment key={step}>
                                                    <span className={`px-1.5 py-0.5 rounded text-[10px] ${isDark ? 'bg-[var(--surface-raised)]' : 'bg-gray-100'} ${textMain}`}>{step}</span>
                                                    {i < arr.length - 1 && <ArrowRight size={9} className={textSub} />}
                                                </React.Fragment>
                                            ))}
                                        </div>
                                        <div className="flex items-center gap-1 flex-wrap">
                                            <span className={`font-semibold text-blue-500 w-16 flex-shrink-0`}>Llegadas</span>
                                            {['AMAN Seq.', 'Asign. Puerta', 'AMS coordina', 'Gate'].map((step, i, arr) => (
                                                <React.Fragment key={step}>
                                                    <span className={`px-1.5 py-0.5 rounded text-[10px] ${isDark ? 'bg-[var(--surface-raised)]' : 'bg-gray-100'} ${textMain}`}>{step}</span>
                                                    {i < arr.length - 1 && <ArrowRight size={9} className={textSub} />}
                                                </React.Fragment>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                {/* Smart Sequencing */}
                                <div className="mb-4">
                                    <label className={`block text-xs font-semibold mb-2 ${textSub} uppercase tracking-wider`}>
                                        Smart Sequencing
                                    </label>
                                    <PillGroup
                                        options={SMART_SEQ_OPTIONS}
                                        value={config.ams_config.smart_sequencing_mode}
                                        onChange={(v) => updateConfig('ams_config.smart_sequencing_mode', v)}
                                    />
                                    {config.ams_config.smart_sequencing_mode && (
                                        <p className={`mt-1.5 text-xs ${textSub}`}>
                                            {config.ams_config.smart_sequencing_mode === 'EOBT' && 'Secuencia por Estimated Off-Block Time declarado.'}
                                            {config.ams_config.smart_sequencing_mode === 'CTOT' && 'Secuencia por Calculated Take-Off Time de red de afluencia.'}
                                            {config.ams_config.smart_sequencing_mode === 'FCFS' && 'First Come, First Served — orden de llegada a posición.'}
                                            {config.ams_config.smart_sequencing_mode === 'ASAT' && 'Approved Start-Up / Target Start-Up Time asignado por ATC.'}
                                        </p>
                                    )}
                                </div>

                                {/* Handover Latency */}
                                <div className="mb-5">
                                    <label className={`block text-xs font-semibold mb-1 ${textSub} uppercase tracking-wider`}>
                                        {language === 'es' ? 'Reducción Latencia Transferencia (%)' : 'Handover Latency Reduction (%)'}
                                    </label>
                                    <input
                                        type="number"
                                        step="5"
                                        min="0"
                                        max="100"
                                        value={Math.round(config.ams_config.handover_latency_reduction * 100)}
                                        onChange={(e) => updateConfig('ams_config.handover_latency_reduction', parseFloat(e.target.value) / 100)}
                                        className={`w-28 px-3 py-1.5 rounded border ${inputBg} ${inputText} text-sm`}
                                    />
                                </div>

                                {/* SOP sub-section */}
                                <div className="pt-4 border-t border-[var(--border)]">
                                    <div className={`flex items-center gap-2 mb-4`}>
                                        <Sliders size={14} className="text-[var(--text-secondary)]" />
                                        <span className={`text-xs font-semibold uppercase tracking-wider ${textSub}`}>
                                            {language === 'es' ? 'Procedimientos Operativos (SOP)' : 'Operating Procedures (SOP)'}
                                        </span>
                                    </div>

                                    <div className="space-y-3">
                                        {/* P/B options */}
                                        <Toggle
                                            enabled={config.ams_config.simultaneous_pushback}
                                            onToggle={() => updateConfig('ams_config.simultaneous_pushback', !config.ams_config.simultaneous_pushback)}
                                            label={language === 'es' ? 'P/B Simultáneo' : 'Simultaneous Pushback'}
                                            description={language === 'es' ? 'Dos o más aeronaves realizan pushback al mismo tiempo en posiciones no contiguas.' : 'Two or more aircraft push back simultaneously on non-adjacent stands.'}
                                        />
                                        <Toggle
                                            enabled={config.ams_config.contiguous_pushback}
                                            onToggle={() => updateConfig('ams_config.contiguous_pushback', !config.ams_config.contiguous_pushback)}
                                            label={language === 'es' ? 'P/B Contiguo' : 'Contiguous Pushback'}
                                            description={language === 'es' ? 'Pushback en posiciones adyacentes con separación mínima garantizada.' : 'Pushback on adjacent stands with guaranteed minimum separation.'}
                                        />
                                        <Toggle
                                            enabled={config.ams_config.simultaneous_contiguous_pushback}
                                            onToggle={() => updateConfig('ams_config.simultaneous_contiguous_pushback', !config.ams_config.simultaneous_contiguous_pushback)}
                                            label={language === 'es' ? 'P/B Simultáneo + Contiguo' : 'Simultaneous + Contiguous Pushback'}
                                            description={language === 'es' ? 'Combinación de ambos modos; requiere AMS con visión completa de plataforma.' : 'Combined mode; requires full-apron AMS situational awareness.'}
                                        />

                                        {/* Dynamic routing */}
                                        <Toggle
                                            enabled={config.ams_config.dynamic_routing}
                                            onToggle={() => updateConfig('ams_config.dynamic_routing', !config.ams_config.dynamic_routing)}
                                            label={language === 'es' ? 'Rutas Dinámicas' : 'Dynamic Routing'}
                                            description={language === 'es' ? 'Rutas asignadas en tiempo real según ocupación de calles de rodaje.' : 'Routes assigned in real time based on taxiway occupancy.'}
                                        />

                                        {/* Adaptive routing / anti-blocking */}
                                        <Toggle
                                            enabled={config.ams_config.adaptive_routing}
                                            onToggle={() => updateConfig('ams_config.adaptive_routing', !config.ams_config.adaptive_routing)}
                                            label={language === 'es' ? 'Ruta Alternativa Anti-bloqueo (TWY contiguas)' : 'Anti-blocking Re-routing (Contiguous TWYs)'}
                                            description={language === 'es'
                                                ? 'AMS asigna eje de TWY alternativo en calles contiguas y paralelas para adelantar un tráfico bloqueado. Solo válido entre TWYs físicamente adyacentes (ej. S1↔S2↔S3 en PTY). No aplica entre calles separadas por estacionamientos o isletas.'
                                                : 'AMS assigns an alternative TWY axis on contiguous parallel taxiways to overtake a blocked traffic. Only valid between physically adjacent TWYs (e.g. S1↔S2↔S3 at PTY). Does not apply between taxiways separated by stands or medians.'}
                                        />

                                        {/* Safety blocking */}
                                        <Toggle
                                            enabled={config.ams_config.safety_blocking}
                                            onToggle={() => updateConfig('ams_config.safety_blocking', !config.ams_config.safety_blocking)}
                                            label={language === 'es' ? 'Safety Blocking (CI/MHT)' : 'Safety Blocking (CI/MHT)'}
                                            description={language === 'es' ? 'Bloqueo de seguridad ante conflictos de infraestructura o riesgo MHT.' : 'Safety block on infrastructure conflicts or MHT risk.'}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* FASTGate */}
                    <div>
                        <SectionHeader
                            icon={Lightning}
                            title={language === 'es' ? 'Con FASTGate (WheelTug)' : 'With FASTGate (WheelTug)'}
                            subtitle={language === 'es' ? 'Reducción ~70% tiempo pushback' : '~70% pushback time reduction'}
                            enabled={config.fastgate_enabled}
                            onToggle={() => updateConfig('fastgate_enabled', !config.fastgate_enabled)}
                            expanded={expandedSections.fastgate}
                            onExpand={() => toggleSection('fastgate')}
                            color="blue"
                        />
                        {config.fastgate_enabled && expandedSections.fastgate && (
                            <div className={`mt-2 p-4 rounded-lg border ml-4 ${panelBg}`}>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <label className={`block text-xs font-medium mb-1 ${textSub}`}>
                                            {language === 'es' ? 'Reducción Pushback (%)' : 'Pushback Reduction (%)'}
                                        </label>
                                        <input
                                            type="number"
                                            step="5"
                                            min="0"
                                            max="100"
                                            value={Math.round(config.fastgate_config.pushback_reduction * 100)}
                                            onChange={(e) => updateConfig('fastgate_config.pushback_reduction', parseFloat(e.target.value) / 100)}
                                            className={`w-full px-3 py-2 rounded border ${inputBg} ${inputText} text-sm`}
                                        />
                                    </div>
                                    <div>
                                        <label className={`block text-xs font-medium mb-1 ${textSub}`}>
                                            {language === 'es' ? 'Tipos Elegibles' : 'Eligible Types'}
                                        </label>
                                        <input
                                            type="text"
                                            value={config.fastgate_config.eligible_types.join(', ')}
                                            onChange={(e) => updateConfig('fastgate_config.eligible_types', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                                            placeholder="A320, A321, B737, B738"
                                            className={`w-full px-3 py-2 rounded border ${inputBg} ${inputText} text-sm`}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Turnaround Monitoring */}
                    <div>
                        <SectionHeader
                            icon={Gauge}
                            title="Turnaround Monitoring Tool"
                            subtitle={language === 'es' ? 'Monitoreo turnaround en tiempo real' : 'Real-time turnaround monitoring'}
                            enabled={config.turnaround_monitoring_enabled}
                            onToggle={() => updateConfig('turnaround_monitoring_enabled', !config.turnaround_monitoring_enabled)}
                            expanded={expandedSections.turnaround}
                            onExpand={() => toggleSection('turnaround')}
                            color="purple"
                        />
                        {config.turnaround_monitoring_enabled && expandedSections.turnaround && (
                            <div className={`mt-2 p-4 rounded-lg border ml-4 ${panelBg}`}>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className={`block text-xs font-medium mb-1 ${textSub}`}>Provider</label>
                                        <select
                                            value={config.turnaround_monitoring_config.provider}
                                            onChange={(e) => updateConfig('turnaround_monitoring_config.provider', e.target.value)}
                                            className={`w-full px-3 py-2 rounded border ${inputBg} ${inputText} text-sm`}
                                        >
                                            <option value="assaia">Turnaround Monitoring Tool</option>
                                            <option value="zestiot">ZestIOT</option>
                                            <option value="other">Other</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className={`block text-xs font-medium mb-1 ${textSub}`}>
                                            {language === 'es' ? 'Reducción Turnaround (%)' : 'Turnaround Reduction (%)'}
                                        </label>
                                        <input
                                            type="number"
                                            step="5"
                                            min="0"
                                            max="50"
                                            value={Math.round(config.turnaround_monitoring_config.turnaround_reduction * 100)}
                                            onChange={(e) => updateConfig('turnaround_monitoring_config.turnaround_reduction', parseFloat(e.target.value) / 100)}
                                            className={`w-full px-3 py-2 rounded border ${inputBg} ${inputText} text-sm`}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Infrastructure */}
                    <div>
                        <SectionHeader
                            icon={Buildings}
                            title={language === 'es' ? 'Con Modificaciones de Infraestructura' : 'With Infrastructure Modifications'}
                            subtitle={language === 'es' ? 'Terminal, plataforma, calles de rodaje, pistas' : 'Terminal, apron, taxiways, runways'}
                            enabled={config.infra_changes_enabled}
                            onToggle={() => updateConfig('infra_changes_enabled', !config.infra_changes_enabled)}
                            expanded={expandedSections.infra}
                            onExpand={() => toggleSection('infra')}
                            color="orange"
                        />
                        {config.infra_changes_enabled && expandedSections.infra && (
                            <div className={`mt-2 p-4 rounded-lg border ml-4 ${panelBg}`}>
                                {/* Type checkboxes */}
                                <div className="mb-4">
                                    <label className={`block text-xs font-semibold mb-2 uppercase tracking-wider ${textSub}`}>
                                        {language === 'es' ? 'Tipo de infraestructura afectada' : 'Type of infrastructure affected'}
                                    </label>
                                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                        {INFRA_TYPES.map(type => {
                                            const checked = config.infra_changes_config.infrastructure_types.includes(type);
                                            return (
                                                <label key={type} className="flex items-center gap-2 cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        checked={checked}
                                                        onChange={(e) => {
                                                            const types = e.target.checked
                                                                ? [...config.infra_changes_config.infrastructure_types, type]
                                                                : config.infra_changes_config.infrastructure_types.filter(t => t !== type);
                                                            updateConfig('infra_changes_config.infrastructure_types', types);
                                                        }}
                                                        className="accent-[#F26430]"
                                                    />
                                                    <span className={`text-xs ${textMain}`}>{type}</span>
                                                </label>
                                            );
                                        })}
                                    </div>
                                </div>

                                {/* Description fields */}
                                <div className="space-y-3">
                                    {[
                                        { key: 'what_improves', label: language === 'es' ? '¿Qué mejora?' : 'What improves?' },
                                        { key: 'how_improves', label: language === 'es' ? '¿Cómo mejora?' : 'How does it improve?' },
                                        { key: 'magnitude', label: language === 'es' ? '¿Cuánto mejora?' : 'How much does it improve?' },
                                    ].map(({ key, label }) => (
                                        <div key={key}>
                                            <label className={`block text-xs font-medium mb-1 ${textSub}`}>{label}</label>
                                            <textarea
                                                value={(config.infra_changes_config as any)[key] || ''}
                                                onChange={(e) => updateConfig(`infra_changes_config.${key}`, e.target.value)}
                                                rows={2}
                                                className={`w-full px-3 py-2 rounded border ${inputBg} ${inputText} text-xs resize-none`}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Standard Routes */}
                    <div>
                        <SectionHeader
                            icon={MapTrifold}
                            title={language === 'es' ? 'Con Rutas Estándar (AIP AD2)' : 'With Standard Routes (AIP AD2)'}
                            subtitle={language === 'es' ? 'Rutas de rodaje publicadas en AIP AD2 del aeródromo' : 'Published taxi routes from aerodrome AIP AD2'}
                            enabled={config.standard_routes_enabled}
                            onToggle={() => updateConfig('standard_routes_enabled', !config.standard_routes_enabled)}
                            expanded={expandedSections.standard_routes}
                            onExpand={() => toggleSection('standard_routes')}
                            color="teal"
                        />
                        {config.standard_routes_enabled && expandedSections.standard_routes && (
                            <div className={`mt-2 p-4 rounded-lg border ml-4 ${panelBg}`}>
                                <label className={`block text-xs font-medium mb-1 ${textSub}`}>
                                    {language === 'es'
                                        ? 'Descripción de rutas estándar (lenguaje natural)'
                                        : 'Standard routes description (natural language)'}
                                </label>
                                <textarea
                                    value={config.standard_routes_config.routes_description}
                                    onChange={(e) => updateConfig('standard_routes_config.routes_description', e.target.value)}
                                    rows={5}
                                    placeholder={language === 'es'
                                        ? 'Ej: Aeronaves de pista 03R al stand T1 usan TWY A → B → C. Aeronaves de pista 21L al stand P2 usan TWY D → E...'
                                        : 'Ex: Aircraft from RWY 03R to stand T1 use TWY A → B → C. Aircraft from RWY 21L to stand P2 use TWY D → E...'}
                                    className={`w-full px-3 py-2 rounded border ${inputBg} ${inputText} text-xs resize-none`}
                                />
                            </div>
                        )}
                    </div>

                    {/* DACAMS Improvements */}
                    <div className={`p-4 rounded-lg border ${cardBg} ${config.dacams_improvements_enabled ? 'ring-2 ring-rose-500/40' : ''}`}>
                        <div className="flex items-center gap-4">
                            <div className={`p-3 rounded-lg flex-shrink-0 ${
                                config.dacams_improvements_enabled
                                    ? 'bg-rose-500/20 text-rose-500'
                                    : isDark ? 'bg-[var(--surface-inset)] text-[var(--text-secondary)]' : 'bg-gray-100 text-gray-400'
                            }`}>
                                <TrendUp size={22} weight="duotone" />
                            </div>
                            <div className="flex-1">
                                <Toggle
                                    enabled={config.dacams_improvements_enabled}
                                    onToggle={() => updateConfig('dacams_improvements_enabled', !config.dacams_improvements_enabled)}
                                    label={language === 'es' ? 'Con Mejoras DACAMS' : 'With DACAMS Improvements'}
                                    description={language === 'es' ? 'Aplicar recomendaciones derivadas del análisis DACAMS' : 'Apply recommendations derived from DACAMS analysis'}
                                />
                            </div>
                        </div>
                    </div>

                </div>
            </div>

            {/* SUMMARY */}
            <div className={`p-4 rounded-xl border ${isDark ? 'bg-emerald-900/20 border-emerald-500/30' : 'bg-emerald-50 border-emerald-200'}`}>
                <div className="flex items-center gap-2 mb-2">
                    <Info size={16} className="text-emerald-500" />
                    <span className={`text-sm font-bold ${isDark ? 'text-emerald-400' : 'text-emerald-700'}`}>
                        {language === 'es' ? 'Resumen de Configuración' : 'Configuration Summary'}
                    </span>
                </div>
                <div className="flex flex-wrap gap-2">
                    {config.is_baseline && (
                        <span className="px-2 py-1 bg-gray-500/20 rounded text-xs font-medium text-gray-500">
                            Baseline (As-Is)
                        </span>
                    )}
                    {config.ams_enabled && (
                        <span className="px-2 py-1 bg-emerald-500/20 rounded text-xs text-emerald-600 dark:text-emerald-400">
                            AMS{config.ams_config.smart_sequencing_mode ? ` · ${config.ams_config.smart_sequencing_mode}` : ''}
                        </span>
                    )}
                    {config.fastgate_enabled && (
                        <span className="px-2 py-1 bg-blue-500/20 rounded text-xs text-blue-600">FASTGate</span>
                    )}
                    {config.turnaround_monitoring_enabled && (
                        <span className="px-2 py-1 bg-purple-500/20 rounded text-xs text-purple-600">
                            {config.turnaround_monitoring_config.provider}
                        </span>
                    )}
                    {config.infra_changes_enabled && (
                        <span className="px-2 py-1 bg-orange-500/20 rounded text-xs text-orange-600">
                            Infra{config.infra_changes_config.infrastructure_types.length > 0
                                ? ` (${config.infra_changes_config.infrastructure_types.join(', ')})`
                                : ''}
                        </span>
                    )}
                    {config.standard_routes_enabled && (
                        <span className="px-2 py-1 bg-teal-500/20 rounded text-xs text-teal-600">Rutas Std.</span>
                    )}
                    {config.dacams_improvements_enabled && (
                        <span className="px-2 py-1 bg-rose-500/20 rounded text-xs text-rose-600">DACAMS</span>
                    )}
                    {!config.is_baseline && !config.ams_enabled && !config.fastgate_enabled && !config.turnaround_monitoring_enabled && !config.infra_changes_enabled && !config.standard_routes_enabled && !config.dacams_improvements_enabled && (
                        <span className={`text-xs ${textSub}`}>
                            {language === 'es' ? 'No hay opciones seleccionadas' : 'No options selected'}
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ScenarioBuilder;
